import React, { useState } from 'react';
import { GREEN, GREEN_LIGHT, BLACK } from '../constants';
import { card, mono } from '../styles';
import { Btn, Modal, TextInput } from './UI';

const BLOCK_COLORS = {
  yellow: { bg: '#fff9db', border: '#f3e8a8' },
  green: { bg: '#ecfdf3', border: '#b7e4c7' },
  blue: { bg: '#eff6ff', border: '#bfdbfe' },
  pink: { bg: '#fdf2f8', border: '#fbcfe8' },
  gray: { bg: '#f5f5f4', border: '#d6d3d1' },
};

function normalizeItem(item) {
  if (item && item.type) return item;
  return {
    id: item.id || String(Date.now()),
    type: 'text',
    title: item.title || '',
    body: item.desc || '',
    link: item.link || '',
  };
}

function emptyForm(type = 'text') {
  return {
    type,
    title: '',
    body: '',
    link: '',
    imageUrl: '',
    color: 'yellow',
  };
}

function saveReady(form) {
  const f = { ...form };
  if (f.type === 'image') return Boolean(f.imageUrl.trim());
  if (f.type === 'callout') return Boolean(f.body.trim() || f.title.trim());
  return Boolean(f.title.trim() || f.body.trim() || f.link.trim());
}

export default function Section({ sec, editable, onUpdate }) {
  const [open, setOpen]       = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx]  = useState(null);
  const [form, setForm]        = useState(emptyForm());
  const [delIdx, setDelIdx]    = useState(null);

  function openAdd() {
    setForm(emptyForm());
    setEditIdx(null);
    setShowForm(true);
  }

  function openEdit(item, i) {
    setForm({ ...emptyForm(), ...normalizeItem(item) });
    setEditIdx(i);
    setShowForm(true);
  }

  function saveItem() {
    const items = [...(sec.items || [])];
    const next = {
      ...form,
      id: editIdx !== null ? normalizeItem(items[editIdx]).id : String(Date.now()),
      title: form.title.trim(),
      body: form.body.trim(),
      link: form.link.trim(),
      imageUrl: form.imageUrl.trim(),
    };
    if (editIdx !== null) items[editIdx] = next;
    else items.push(next);
    onUpdate(items);
    setShowForm(false);
  }

  const items = sec.items || [];

  return (
    <div style={card({ marginBottom: 8 })}>
      {/* Header row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: open ? GREEN : '#f5f5f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 11, color: '#555', transition: 'background .2s',
          }}>
            {sec.label.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{sec.label}</div>
            <div style={mono}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {editable && (
            <Btn sm v="outline" onClick={e => { e.stopPropagation(); openAdd(); setOpen(true); }}>
              + Add
            </Btn>
          )}
          <div style={{
            width: 26, height: 26, borderRadius: 7, border: '1.5px solid #ddd',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: open ? 'rotate(45deg)' : 'none', transition: 'transform .2s', fontSize: 16,
          }}>+</div>
        </div>
      </div>

      {/* Items */}
      {open && (
        <div style={{ marginTop: 14, borderTop: '1.5px solid #f0f0ec', paddingTop: 14 }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '14px 0', color: '#999', fontSize: 13 }}>
              {editable
                ? <span>No items yet — <button onClick={openAdd} style={{ background: 'none', border: 'none', color: GREEN, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>add one</button></span>
                : 'Your agency is filling this in soon.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {items.map((rawItem, i) => {
                const item = normalizeItem(rawItem);
                const c = BLOCK_COLORS[item.color] || BLOCK_COLORS.yellow;
                const isImage = item.type === 'image';
                const isCallout = item.type === 'callout';
                return (
                  <div
                    key={item.id || i}
                    style={{
                      background: isCallout ? c.bg : '#fff',
                      border: `1.5px solid ${isCallout ? c.border : '#f0f0ec'}`,
                      borderRadius: 10,
                      padding: 12,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        {item.title && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 5 }}>{item.title}</div>}

                        {isImage ? (
                          <div>
                            <img
                              src={item.imageUrl}
                              alt={item.title || 'Uploaded block'}
                              style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8, border: '1px solid #e7e5e4' }}
                            />
                            {item.body && <div style={{ fontSize: 12, color: '#555', lineHeight: 1.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{item.body}</div>}
                          </div>
                        ) : (
                          item.body && <div style={{ fontSize: 12, color: '#555', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{item.body}</div>
                        )}

                        {item.link && (
                          <a href={item.link} target="_blank" rel="noreferrer" style={{
                            fontSize: 11, fontFamily: 'monospace', background: GREEN_LIGHT, color: BLACK,
                            padding: '2px 7px', borderRadius: 4, display: 'inline-block', marginTop: 7, textDecoration: 'none',
                          }}>
                            {item.link.replace(/^https?:\/\//, '').slice(0, 46)}
                          </a>
                        )}
                      </div>
                      {editable && (
                        <div style={{ display: 'flex', gap: 4, marginLeft: 8, flexShrink: 0 }}>
                          <button onClick={() => openEdit(item, i)} style={{ background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', color: '#999' }}>Edit</button>
                          <button onClick={() => setDelIdx(i)} style={{ background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', color: '#dc2626' }}>Del</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>{editIdx !== null ? 'Edit' : 'Add'} Block</div>

          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#999', marginBottom: 6, fontFamily: 'monospace' }}>
            Block Type
          </div>
          <select
            value={form.type}
            onChange={e => setForm(p => ({ ...emptyForm(e.target.value), ...p, type: e.target.value }))}
            style={{ width: '100%', padding: '9px 11px', borderRadius: 9, border: '1.5px solid #e0e0d8', fontSize: 13, marginBottom: 12, fontFamily: 'system-ui, sans-serif' }}
          >
            <option value="text">Text Block</option>
            <option value="image">Image Block</option>
            <option value="callout">Color Block</option>
          </select>

          <TextInput label="Title (optional)" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} ph="e.g. Shopify Admin" />

          {form.type === 'image' && (
            <TextInput
              label="Image URL"
              value={form.imageUrl}
              onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))}
              ph="https://..."
            />
          )}

          {form.type === 'callout' && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#999', marginBottom: 6, fontFamily: 'monospace' }}>
                Color
              </div>
              <select
                value={form.color}
                onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                style={{ width: '100%', padding: '9px 11px', borderRadius: 9, border: '1.5px solid #e0e0d8', fontSize: 13, marginBottom: 12, fontFamily: 'system-ui, sans-serif' }}
              >
                <option value="yellow">Yellow</option>
                <option value="green">Green</option>
                <option value="blue">Blue</option>
                <option value="pink">Pink</option>
                <option value="gray">Gray</option>
              </select>
            </div>
          )}

          <TextInput
            label={form.type === 'image' ? 'Caption (optional)' : 'Content'}
            value={form.body}
            onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
            ph={form.type === 'image' ? 'Optional caption...' : 'Write details...'}
            multi
          />

          {form.type !== 'image' && (
            <TextInput label="Link (optional)" value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))} ph="https://..." />
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn v="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn onClick={saveItem} dis={!saveReady(form)}>Save</Btn>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {delIdx !== null && (
        <Modal onClose={() => setDelIdx(null)}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete this item?</div>
            <div style={{ fontSize: 13, color: '#999', marginBottom: 18 }}>This cannot be undone.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <Btn v="outline" onClick={() => setDelIdx(null)}>Cancel</Btn>
              <Btn v="danger" onClick={() => { onUpdate(items.filter((_, i) => i !== delIdx)); setDelIdx(null); }}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
